import { useState, useRef, useMemo } from 'react';
import { Loader2, Check, Upload, Scan, Eye, Receipt as ReceiptIcon, Plus, Trash2 } from 'lucide-react';
import { createTithe, updateTithe, deleteTithe, replaceTithePayments } from '../data/tithesRepository';
import { supabase } from '@/supabase.config';
import type { Tithe, TitheSubjectType, TithePaymentMethod, TithePaymentInput } from '../types';
import { MONTH_NAMES_FULL } from '../types';
import type { BankAccount } from '@/features/settings/types';
import { prepareVoucherForOcr } from '../utils/prepareVoucherImage';
import { extractVoucherFields } from '../utils/extractVoucherFields';
import {
  useActiveReceiptCategories, useReceiptConcepts, useReceiptBySource, useReceiptMutations,
} from '@/features/receipts/hooks/useReceipts';
import { IssueReceiptForm } from '@/features/receipts/components/IssueReceiptForm';
import { ReceiptPrintPreview } from '@/features/receipts/components/ReceiptPrintPreview';
import { receiptToPrintData, formatReceiptNumber } from '@/features/receipts/types';
import type { ReceiptPaymentInput, ReceiptCurrency } from '@/features/receipts/types';
import { amountToWords } from '@/features/receipts/utils/numberToWords';
import { useToast } from '@/hooks/use-toast';
import { publicReceiptUrl } from '@/features/receipts/utils/publicReceiptUrl';
import { formatMoney } from '@/features/receipts/utils/receiptFormat';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface TithePaymentFormProps {
  subjectId: string;
  subjectType: TitheSubjectType;
  contextLine1: string;
  contextLine2: string;
  year: number;
  month: number;
  existing?: Tithe | null;
  /** Position rows: the minister, for linking and prefilling the receipt */
  ministerId?: string;
  payerCi?: string | null;
  bankAccounts: BankAccount[];
  onClose: () => void;
  onSaved: () => void;
}

/** Which receipt category/concept a tithe of each kind of subject is issued under. */
const RECEIPT_TARGETS: Record<TitheSubjectType, { categoryCode: string; conceptCode: string; label: string }> = {
  position:    { categoryCode: 'DZM', conceptCode: 'DIEZMOS',             label: 'Diezmo' },
  district:    { categoryCode: 'ADI', conceptCode: 'APORTE_DISTRITO',     label: 'Aporte distrital' },
  region:      { categoryCode: 'ARE', conceptCode: 'APORTE_REGION',       label: 'Aporte regional' },
  institution: { categoryCode: 'ADP', conceptCode: 'APORTE_DEPARTAMENTO', label: 'Aporte de departamento' },
};

const INPUT   = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const INPUT_S = 'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const SECTION = 'text-xs font-semibold uppercase tracking-wide text-muted-foreground';

export function TithePaymentForm({
  subjectId,
  subjectType,
  contextLine1,
  contextLine2,
  year,
  month,
  existing,
  ministerId,
  payerCi,
  bankAccounts,
  onClose,
  onSaved,
}: TithePaymentFormProps) {
  const [amount, setAmount]           = useState(existing ? String(existing.amount) : '');
  const [paidAt, setPaidAt]           = useState(existing?.paid_at ?? '');
  const [notes, setNotes]             = useState(existing?.notes ?? '');
  const [payerName, setPayerName]     = useState(existing?.payer_name ?? '');
  const [payments, setPayments]       = useState<TithePaymentInput[]>(
    existing?.payments && existing.payments.length > 0
      ? existing.payments.map((p) => ({
          method: p.method,
          amount: String(p.amount),
          bank_account_id: p.bank_account_id ?? '',
          check_number: p.check_number ?? '',
        }))
      : [{ method: '', amount: '', bank_account_id: '', check_number: '' }],
  );
  // A single payment line mirrors the main amount until the user types in it themselves.
  const [paymentAmountTouched, setPaymentAmountTouched] = useState(!!existing?.payments?.length);
  const [amountUsd, setAmountUsd]     = useState(existing?.amount_usd ? String(existing.amount_usd) : '');
  const [exchangeRate, setExchangeRate] = useState(existing?.exchange_rate ? String(existing.exchange_rate) : '');

  // Voucher
  const [voucherFile, setVoucherFile]           = useState<File | null>(null);
  const [voucherPreview, setVoucherPreview]     = useState<string | null>(existing?.voucher_photo_url ?? null);
  const [previewFailed, setPreviewFailed]       = useState(false);
  const existingVoucherUrl                      = existing?.voucher_photo_url ?? '';
  const [txRef, setTxRef]                       = useState(existing?.voucher_transaction_ref ?? '');
  const [depositorName, setDepositorName]       = useState(existing?.voucher_depositor_name ?? '');
  const [destAccount, setDestAccount]           = useState(existing?.voucher_dest_account ?? '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadingVoucher, setUploadingVoucher] = useState(false);
  const [extractingOcr, setExtractingOcr]       = useState(false);
  const [ocrMsg, setOcrMsg] = useState<{ kind: 'ok' | 'warn'; text: string } | null>(null);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [showUsd, setShowUsd]                  = useState(!!existing?.amount_usd);
  const [showVoucherDetails, setShowVoucherDetails] = useState(false);
  // Names of inputs just filled by OCR, highlighted briefly so they get double-checked.
  const [ocrFilled, setOcrFilled]               = useState<Set<string>>(new Set());
  const [justCreated, setJustCreated]           = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [deleting, setDeleting]                 = useState(false);
  const [error, setError]                       = useState('');

  // Set when this form created a row but a later step failed, so a retry
  // updates that row instead of inserting a duplicate.
  const [createdId, setCreatedId] = useState<string | null>(null);

  // Receipt (issued via the receipts module, linked by source_type='tithe')
  const { toast } = useToast();
  const { issue } = useReceiptMutations();
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [showReceiptPreview, setShowReceiptPreview] = useState(false);
  const [confirmDelete, setConfirmDelete]           = useState(false);
  const { data: receiptCategories = [] } = useActiveReceiptCategories();
  const { data: receiptConcepts = [] } = useReceiptConcepts();
  // Falls back to the id from a just-completed creation, so the receipt
  // actions appear immediately without waiting on the parent to refetch and
  // pass a fresh `existing` back down.
  const receiptSourceId = existing?.id ?? createdId ?? undefined;
  const {
    data: linkedReceipt, isLoading: linkedReceiptLoading, refetch: refetchLinkedReceipt,
  } = useReceiptBySource('tithe', receiptSourceId);
  // A voided receipt no longer counts: it can be replaced and doesn't block deletion.
  const issuedReceipt = linkedReceipt?.status === 'issued' ? linkedReceipt : null;
  const voidedReceipt = linkedReceipt?.status === 'void' ? linkedReceipt : null;
  // Position tithes are Diezmos; district / region / department rows are aportes.
  const receiptTarget = RECEIPT_TARGETS[subjectType];
  const receiptCategory = receiptCategories.find((c) => c.code === receiptTarget.categoryCode);
  const receiptConcept = receiptConcepts.find((c) => c.code === receiptTarget.conceptCode);

  const isEditing = !!existing;
  // After the first save the dialog stays open as an edit of the new row.
  const titleEditing = isEditing || !!createdId;
  const busy = saving || deleting || uploadingVoucher || extractingOcr;
  const hasVoucher = !!voucherFile || !!existingVoucherUrl;

  const validPayments = payments.filter((p) => parseFloat(p.amount) > 0);
  const paymentsSum = validPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
  const hasTransferPayment = validPayments.some((p) => p.method === 'bank_transfer');

  // Carries the tithe's own payment lines into the receipt so the user
  // doesn't have to re-enter what was already captured above.
  // A tithe paid in dollars is issued as a USD receipt: the dollar amount becomes the
  // line, and the (Bs.) payment lines are converted at the same exchange rate.
  const receiptRate = parseFloat(exchangeRate);
  const receiptUsdTotal = parseFloat(amountUsd);
  const receiptUsd = receiptUsdTotal > 0 && receiptRate > 0;
  const receiptCurrency: ReceiptCurrency = receiptUsd ? 'USD' : 'BOB';
  const receiptTotal = receiptUsd ? receiptUsdTotal.toFixed(2) : amount;

  const initialReceiptPayments = useMemo<ReceiptPaymentInput[]>(() => {
    const lines = validPayments
      .filter((p): p is TithePaymentInput & { method: TithePaymentMethod } => !!p.method)
      .map((p) => ({
        method: p.method,
        amount: receiptUsd ? (parseFloat(p.amount) / receiptRate).toFixed(2) : p.amount,
        bank_account_id: p.bank_account_id,
        check_number: p.check_number,
        voucher_photo_url: '',
      }));
    if (receiptUsd && lines.length > 0) {
      // Rounding each line can leave the sum a cent off the USD total; absorb it in the last line.
      const diff = receiptUsdTotal - lines.reduce((s, l) => s + parseFloat(l.amount), 0);
      if (Math.abs(diff) > 0.001) {
        const last = lines[lines.length - 1];
        last.amount = (parseFloat(last.amount) + diff).toFixed(2);
      }
    }
    return lines;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, receiptUsd, receiptRate, receiptUsdTotal]);

  const updatePayment = (i: number, patch: Partial<TithePaymentInput>) =>
    setPayments((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const ocrRing = (name: string) => (ocrFilled.has(name) ? 'ring-2 ring-success/60' : '');
  const clearOcr = (name: string) =>
    setOcrFilled((prev) => { if (!prev.has(name)) return prev; const n = new Set(prev); n.delete(name); return n; });

  const handleAmountChange = (value: string) => {
    setAmount(value);
    clearOcr('amount');
    // Only once a method is picked, so an untouched line never turns "Forma de pago" into a required field.
    if (payments.length === 1 && !paymentAmountTouched && payments[0].method) updatePayment(0, { amount: value });
  };
  const handleMethodChange = (i: number, method: TithePaymentMethod | '') => {
    const mirror = payments.length === 1 && !paymentAmountTouched && !payments[i].amount && !!method;
    updatePayment(i, mirror ? { method, amount } : { method });
  };
  const addPayment = () => {
    setPaymentAmountTouched(true);
    // A split almost always mixes methods, so the new line starts as the other one.
    setPayments((prev) => {
      const last = prev[prev.length - 1]?.method;
      const method = last === 'cash' ? 'bank_transfer' : last === 'bank_transfer' ? 'cash' : '';
      return [...prev, { method, amount: '', bank_account_id: '', check_number: '' }];
    });
  };
  const removePayment = (i: number) => {
    setPaymentAmountTouched(true);
    setPayments((prev) => prev.filter((_, idx) => idx !== i));
  };

  /** What still blocks saving. Drives both the disabled button and the hint. */
  const missing = useMemo(() => {
    const items: string[] = [];
    const parsed = parseFloat(amount);
    if (!amount || isNaN(parsed) || parsed <= 0) items.push('Monto (Bs.) mayor a 0');
    if (!paidAt) items.push('Fecha de pago');
    if (validPayments.length > 0 && parsed > 0 && Math.abs(paymentsSum - parsed) > 0.01) {
      items.push(`La forma de pago (${paymentsSum.toFixed(2)}) debe sumar el monto (${parsed.toFixed(2)})`);
    }
    payments.forEach((p, i) => {
      const lineAmount = parseFloat(p.amount);
      if (!(lineAmount > 0)) return;
      if (!p.method) items.push(`Tipo de pago (línea ${i + 1})`);
      if (p.method === 'check' && !p.check_number.trim()) items.push(`N° de cheque (línea ${i + 1})`);
      if (p.method === 'bank_transfer' && bankAccounts.length > 0 && !p.bank_account_id) {
        items.push(`Cuenta bancaria destino (línea ${i + 1})`);
      }
    });
    if ((hasVoucher || hasTransferPayment) && !txRef.trim()) items.push('N° de transacción del comprobante');
    // The DB rejects one without the other (tithes_usd_fields_consistent).
    if (!!amountUsd !== !!exchangeRate) items.push('Monto (USD) y T/C: ambos o ninguno');
    return items;
  }, [amount, paidAt, payments, validPayments.length, paymentsSum, bankAccounts.length,
      hasVoucher, hasTransferPayment, txRef, amountUsd, exchangeRate]);

  const isValid = missing.length === 0;

  const issuedReceiptNumber = issuedReceipt
    ? formatReceiptNumber(issuedReceipt.number)
    : '';
  const parsedAmount = parseFloat(amount);
  const amountDiverges = !!issuedReceipt && parsedAmount > 0
    && Math.abs(issuedReceipt.total - parsedAmount) > 0.01;

  const handleVoucherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setVoucherFile(file);
    setPreviewFailed(false);
    if (file) {
      setVoucherPreview(URL.createObjectURL(file));
      // A voucher means the money came in by transfer/QR; save the user picking it.
      if (payments.length === 1 && !payments[0].method) {
        updatePayment(0, { method: 'bank_transfer', amount: payments[0].amount || amount });
      }
    }
  };

  const uploadVoucher = async (titheId: string, file: File): Promise<string> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;

    const { data: urlData, error: fnErr } = await supabase.functions.invoke(
      'generate-r2-upload-url',
      { body: { fileName: file.name, contentType: file.type, titheId } }
    );
    if (fnErr) throw fnErr;

    const res = await fetch(urlData.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!res.ok) throw new Error('R2 upload failed');

    // If user is authenticated, pass token for RLS-guarded update
    void token;
    return urlData.fileUrl as string;
  };

  const handleExtractOcr = async () => {
    const file = voucherFile;
    if (!file) return;
    setExtractingOcr(true);
    setOcrProgress(0);
    setOcrMsg(null);
    try {
      // Downscaled copy only — the original file is what gets uploaded to R2.
      const dataUrl = await prepareVoucherForOcr(file);
      const { fields, rawText } = await extractVoucherFields(dataUrl, setOcrProgress);
      // Logged so the label patterns in parseVoucherText can be tuned against
      // what real receipts actually produce.
      if (import.meta.env.DEV) {
        console.log('[OCR] texto reconocido:\n', rawText);
        console.log('[OCR] campos extraídos:', fields);
      }

      let filled = 0;
      const filledNames = new Set<string>();
      const mark = (name: string) => { filled++; filledNames.add(name); };
      if (fields.transactionRef) { setTxRef(fields.transactionRef); mark('txRef'); }
      if (fields.depositorName)  {
        setDepositorName(fields.depositorName); mark('depositorName');
        if (!payerName.trim()) { setPayerName(fields.depositorName); mark('payerName'); }
      }
      if (fields.destAccount)    { setDestAccount(fields.destAccount); mark('destAccount'); }
      if (fields.amount && !amount)  { setAmount(fields.amount); mark('amount'); }
      if (fields.date && !paidAt)    { setPaidAt(fields.date); mark('paidAt'); }
      if (fields.bank) {
        const bankName = fields.bank.toLowerCase();
        const match = bankAccounts.find((b) => b.bank_name.toLowerCase().includes(bankName));
        // Only fills a transfer line the user already started — OCR shouldn't
        // invent a payment method on its own.
        const targetIdx = payments.findIndex((p) => p.method === 'bank_transfer' && !p.bank_account_id);
        if (match && targetIdx !== -1) {
          updatePayment(targetIdx, { bank_account_id: match.id });
          filled++;
        }
      }
      setOcrFilled(filledNames);
      window.setTimeout(() => setOcrFilled(new Set()), 4000);
      if (filledNames.has('txRef') || filledNames.has('depositorName') || filledNames.has('destAccount')) {
        setShowVoucherDetails(true);
      }

      setOcrMsg(filled > 0
        ? { kind: 'ok', text: `${filled} campo(s) completado(s). Verifica los datos.` }
        : { kind: 'warn', text: 'No se pudo leer el comprobante. Suele fallar con textos manuscritos — ingresa los datos manualmente.' });
    } catch (e) {
      console.error(e);
      setOcrMsg({ kind: 'warn', text: 'No se pudo procesar la imagen. Ingresa los datos manualmente.' });
    } finally {
      setExtractingOcr(false);
      setOcrProgress(0);
    }
  };

  const handleSave = async () => {
    if (!isValid) return;

    setSaving(true);
    setError('');
    try {
      const receiptFields = {
        payer_name:              payerName || null,
        amount_usd:              amountUsd ? parseFloat(amountUsd) : null,
        exchange_rate:           exchangeRate ? parseFloat(exchangeRate) : null,
        voucher_transaction_ref: txRef || null,
        voucher_depositor_name:  depositorName || null,
        voucher_dest_account:    destAccount || null,
      };

      // A previous attempt may have created the row and then failed on the
      // voucher upload. Reuse that id so a retry updates instead of inserting a
      // second row, which the per-subject-per-month unique index rejects.
      const knownId = existing?.id ?? createdId;
      const wasNew = !knownId;

      let titheId: string;
      if (knownId) {
        await updateTithe(knownId, {
          amount:  parsedAmount,
          paid_at: paidAt,
          notes:   notes || null,
          ...receiptFields,
        });
        titheId = knownId;
      } else {
        titheId = await createTithe({
          subjectType,
          subjectId,
          year,
          month,
          amount,
          paid_at: paidAt,
          notes,
          payer_name: payerName,
          amount_usd: amountUsd,
          exchange_rate: exchangeRate,
          voucher_photo_url: '',
          voucher_transaction_ref: txRef,
          voucher_depositor_name: depositorName,
          voucher_dest_account: destAccount,
        });
        setCreatedId(titheId);
      }

      // From here on, the tithe row itself is saved. Neither of the following
      // steps failing should throw that away — each downgrades to a warning
      // and the record stands, matching the voucher-upload resilience below.
      let paymentsFailed = false;
      try {
        await replaceTithePayments(titheId, payments);
      } catch (err) {
        console.error(err);
        paymentsFailed = true;
      }

      let voucherFailed = false;
      if (voucherFile) {
        setUploadingVoucher(true);
        try {
          const url = await uploadVoucher(titheId, voucherFile);
          await updateTithe(titheId, { voucher_photo_url: url });
        } catch (err) {
          console.error(err);
          voucherFailed = true;
        } finally {
          setUploadingVoucher(false);
        }
      }

      onSaved();
      if (voucherFailed || paymentsFailed) {
        const failedParts = [
          paymentsFailed && 'la forma de pago',
          voucherFailed && 'el comprobante',
        ].filter(Boolean).join(' ni ');
        setError(`El diezmo se guardó, pero no se pudo guardar ${failedParts}. Vuelve a intentarlo.`);
      } else if (wasNew) {
        // Keep the dialog open on first registration so the user can
        // immediately issue the receipt, instead of reopening the cell.
        setJustCreated(true);
      } else {
        onClose();
      }
    } catch (e) {
      console.error(e);
      setError('Error al guardar. Intenta de nuevo.');
      setUploadingVoucher(false);
    } finally {
      setSaving(false);
    }
  };

  const receiptPaymentsSum = initialReceiptPayments.reduce((sum, l) => sum + parseFloat(l.amount), 0);
  // Everything the receipt needs is already on this form; only a missing/mismatched
  // payment split sends the user to the full receipt form.
  const quickIssueReady = initialReceiptPayments.length > 0
    && Math.abs(receiptPaymentsSum - parseFloat(receiptTotal)) <= 0.01;

  /** One-click: issue the receipt from the data already captured, then show it. */
  const handleIssueReceipt = async () => {
    if (!receiptCategory || !receiptSourceId) return;
    if (!quickIssueReady) { setShowIssueForm(true); return; }
    try {
      const result = await issue.mutateAsync({
        input: {
          category_id: receiptCategory.id,
          payer_name: (payerName || contextLine1).trim(),
          payer_ci: payerCi ?? '',
          currency: receiptCurrency,
          exchange_rate: receiptUsd ? exchangeRate : '',
          discount: '',
          source_type: 'tithe',
          source_id: receiptSourceId,
          minister_id: ministerId ?? null,
          lines: [{
            concept_id: receiptConcept?.id ?? '',
            description: `${receiptTarget.label} de ${MONTH_NAMES_FULL[month - 1]} ${year}`,
            quantity: '1',
            unit_price: receiptTotal,
          }],
          payments: initialReceiptPayments,
        },
        totalInWords: amountToWords(parseFloat(receiptTotal), receiptCurrency),
      });
      await refetchLinkedReceipt();
      toast({ title: `Recibo ${formatReceiptNumber(result.number)} emitido` });
      setShowReceiptPreview(true);
    } catch (e) {
      console.error(e);
      toast({ title: 'No se pudo emitir directamente. Revise los datos.', variant: 'destructive' });
      setShowIssueForm(true);
    }
  };

  const handleDelete = async () => {
    if (!existing || issuedReceipt) return;
    setDeleting(true);
    try {
      await deleteTithe(existing.id);
      onSaved();
      onClose();
    } catch {
      setError('Error al eliminar. Intenta de nuevo.');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const showVoucherBlock = showVoucherDetails || hasVoucher || hasTransferPayment
    || !!txRef || !!depositorName || !!destAccount;

  const receiptButton = !receiptSourceId && receiptCategory ? (
    <Button type="button" size="sm" disabled title="Guarde el diezmo para emitir el recibo">
      <ReceiptIcon className="mr-1.5 size-4" />
      Emitir recibo
    </Button>
  ) : receiptSourceId && !linkedReceiptLoading && (
    issuedReceipt ? (
      <Button type="button" variant="outline" size="sm" onClick={() => setShowReceiptPreview(true)}>
        <Eye className="mr-1.5 size-4" />
        Ver recibo {issuedReceiptNumber}
      </Button>
    ) : receiptCategory ? (
      <>
        <Button type="button" size="sm" onClick={handleIssueReceipt} disabled={issue.isPending}>
          {issue.isPending
            ? <Loader2 className="mr-1.5 size-4 animate-spin" />
            : <ReceiptIcon className="mr-1.5 size-4" />}
          Emitir recibo
        </Button>
        <button
          type="button" onClick={() => setShowIssueForm(true)} disabled={issue.isPending}
          className="text-xs text-muted-foreground hover:text-primary hover:underline"
        >
          Editar antes de emitir
        </button>
      </>
    ) : null
  );

  return (
    <>
      {/* Hidden (not unmounted) while a receipt modal is up: those are plain overlays, and an
          open Radix dialog would make them unclickable. Form state lives in this component. */}
      <Dialog
        open={!showIssueForm && !showReceiptPreview}
        onOpenChange={(o) => { if (!o && !busy) onClose(); }}
      >
        <DialogContent
          onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
          className="max-h-[90vh] max-w-xl overflow-y-auto max-sm:h-dvh max-sm:max-h-dvh max-sm:max-w-none max-sm:rounded-none"
        >
          <DialogHeader>
            <DialogTitle>{titleEditing ? 'Editar diezmo' : 'Registrar diezmo'}</DialogTitle>
            <DialogDescription className="sr-only">{contextLine1} — {contextLine2}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
              <p className="font-medium">{contextLine1}</p>
              <p className="text-muted-foreground">{contextLine2}</p>
            </div>

            {justCreated ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-success/40 bg-success/10 px-3 py-2">
                <p className="flex items-center gap-1.5 text-sm font-medium text-success">
                  <Check className="size-4" /> Diezmo registrado.
                </p>
                <div className="flex items-center gap-2">
                  {receiptButton}
                  <Button type="button" variant="outline" size="sm" onClick={onClose}>Cerrar</Button>
                </div>
              </div>
            ) : (
              receiptButton && <div className="flex flex-wrap items-center gap-2">{receiptButton}</div>
            )}

            {voidedReceipt && !issuedReceipt && (
              <p className="text-xs text-muted-foreground">
                Recibo {formatReceiptNumber(voidedReceipt.number)} anulado.
                Puede emitir uno nuevo.
              </p>
            )}

            {/* Amount */}
            <section className="space-y-3">
              <h3 className={SECTION}>Monto</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">Monto (Bs.) <span className="text-destructive">*</span></label>
                  <input
                    type="number" min="0.01" step="0.01" value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)} disabled={busy}
                    placeholder="0.00"
                    className={`${INPUT} ${ocrRing('amount')}`}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Fecha de pago <span className="text-destructive">*</span></label>
                  <input
                    type="date" value={paidAt}
                    onChange={(e) => { setPaidAt(e.target.value); clearOcr('paidAt'); }} disabled={busy}
                    className={`${INPUT} ${ocrRing('paidAt')}`}
                  />
                </div>
              </div>

              {amountDiverges && issuedReceipt && (
                <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                  El recibo {issuedReceiptNumber} fue emitido por {formatMoney(issuedReceipt.total)}.
                  Si cambia el monto, anule y vuelva a emitir el recibo.
                </p>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Depositado por <span className="font-normal text-muted-foreground">(nombre de la persona)</span>
                </label>
                <input
                  value={payerName}
                  onChange={(e) => { setPayerName(e.target.value); clearOcr('payerName'); }} disabled={busy}
                  placeholder="Nombre completo"
                  className={`${INPUT} ${ocrRing('payerName')}`}
                />
              </div>
            </section>

            {/* Payment method(s) — split across lines when paid part cash, part QR, etc. */}
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className={SECTION}>
                  Forma de pago <span className="font-normal normal-case">(opcional)</span>
                </h3>
                <button
                  type="button" onClick={addPayment} disabled={busy}
                  className="flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <Plus className="size-3.5" /> Dividir pago
                </button>
              </div>
              <div className="space-y-2">
                {payments.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 rounded-md border border-border p-2">
                    <select
                      value={p.method}
                      onChange={(e) => handleMethodChange(i, e.target.value as TithePaymentMethod | '')}
                      disabled={busy}
                      aria-label="Tipo de pago"
                      className="col-span-4 rounded-md border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">— Tipo —</option>
                      <option value="bank_transfer">Transferencia</option>
                      <option value="cash">Efectivo</option>
                      <option value="check">Cheque</option>
                    </select>
                    <input
                      type="number" min="0.01" step="0.01" value={p.amount}
                      onChange={(e) => { setPaymentAmountTouched(true); updatePayment(i, { amount: e.target.value }); }}
                      disabled={busy}
                      aria-label="Monto del pago"
                      placeholder="Monto"
                      className="col-span-3 rounded-md border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {p.method === 'check' && (
                      <input
                        value={p.check_number}
                        onChange={(e) => updatePayment(i, { check_number: e.target.value })}
                        disabled={busy}
                        placeholder="N° cheque"
                        className="col-span-4 rounded-md border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    )}
                    {p.method === 'bank_transfer' && bankAccounts.length > 0 && (
                      <select
                        value={p.bank_account_id}
                        onChange={(e) => updatePayment(i, { bank_account_id: e.target.value })}
                        disabled={busy}
                        aria-label="Cuenta bancaria destino"
                        className="col-span-4 rounded-md border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="">Cuenta...</option>
                        {bankAccounts.map((b) => (
                          <option key={b.id} value={b.id}>{b.account_name} — {b.bank_name}</option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      onClick={() => removePayment(i)}
                      disabled={busy || payments.length === 1}
                      aria-label="Quitar línea de pago"
                      className="col-span-1 flex items-center justify-center text-muted-foreground hover:text-destructive disabled:opacity-30"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              {validPayments.length > 0 && Math.abs(paymentsSum - (parseFloat(amount) || 0)) > 0.01 && (
                <p className="text-xs text-muted-foreground">
                  Suma de pagos: {paymentsSum.toFixed(2)} / Monto: {(parseFloat(amount) || 0).toFixed(2)}
                </p>
              )}
            </section>

            {/* Voucher */}
            <section className="space-y-3">
              <h3 className={SECTION}>
                Comprobante de depósito <span className="font-normal normal-case">(opcional)</span>
              </h3>
              <div className="flex items-start gap-3">
                {voucherPreview && !previewFailed && (
                  <a href={voucherPreview} target="_blank" rel="noreferrer">
                    <img
                      src={voucherPreview}
                      alt="Comprobante"
                      onError={() => setPreviewFailed(true)}
                      className="size-20 rounded border object-cover"
                    />
                  </a>
                )}
                {/* A blocked or missing image would otherwise look like "no voucher". */}
                {voucherPreview && previewFailed && (
                  <a
                    href={voucherPreview}
                    target="_blank"
                    rel="noreferrer"
                    className="flex size-20 items-center justify-center rounded border border-dashed border-border p-1 text-center text-[10px] text-muted-foreground hover:text-primary"
                  >
                    No se pudo mostrar. Abrir
                  </a>
                )}
                <div className="flex flex-col gap-2">
                  <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary">
                    <Upload className="size-4" />
                    {voucherPreview ? 'Cambiar imagen' : 'Subir comprobante'}
                    <input
                      ref={fileInputRef}
                      type="file" accept="image/*" className="hidden"
                      onChange={handleVoucherChange} disabled={busy}
                    />
                  </label>
                  {voucherFile && (
                    <button
                      type="button"
                      onClick={handleExtractOcr} disabled={busy}
                      className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
                    >
                      {extractingOcr
                        ? <Loader2 className="size-4 animate-spin" />
                        : <Scan className="size-4" />}
                      {extractingOcr
                        ? `Leyendo… ${ocrProgress}%`
                        : 'Leer datos del comprobante'}
                    </button>
                  )}
                  {ocrMsg && (
                    <p className={`text-xs ${ocrMsg.kind === 'ok' ? 'text-success' : 'text-amber-600 dark:text-amber-500'}`}>
                      {ocrMsg.text}
                    </p>
                  )}
                </div>
              </div>

              {showVoucherBlock ? (
                <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
                  <p className={SECTION}>Datos del comprobante</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium">
                        N° de transacción {(hasVoucher || hasTransferPayment) && <span className="text-destructive">*</span>}
                      </label>
                      <input
                        value={txRef}
                        onChange={(e) => { setTxRef(e.target.value); clearOcr('txRef'); }} disabled={busy}
                        placeholder="—"
                        className={`${INPUT_S} font-mono ${ocrRing('txRef')}`}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium">Titular (según comprobante)</label>
                      <input
                        value={depositorName}
                        onChange={(e) => { setDepositorName(e.target.value); clearOcr('depositorName'); }} disabled={busy}
                        placeholder="—"
                        className={`${INPUT_S} ${ocrRing('depositorName')}`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium">Cuenta destino (comprobante)</label>
                    <input
                      value={destAccount}
                      onChange={(e) => { setDestAccount(e.target.value); clearOcr('destAccount'); }} disabled={busy}
                      placeholder="—"
                      className={`${INPUT_S} font-mono ${ocrRing('destAccount')}`}
                    />
                  </div>
                </div>
              ) : (
                <button
                  type="button" onClick={() => setShowVoucherDetails(true)}
                  className="text-sm text-muted-foreground hover:text-primary hover:underline"
                >
                  + Agregar datos de comprobante
                </button>
              )}
            </section>

            {/* Foreign currency */}
            {showUsd ? (
              <section className="space-y-3">
                <h3 className={SECTION}>Moneda extranjera <span className="font-normal normal-case">(opcional)</span></h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium">Monto (USD)</label>
                    <input
                      type="number" min="0.01" step="0.01" value={amountUsd}
                      onChange={(e) => setAmountUsd(e.target.value)} disabled={busy}
                      placeholder="0.00"
                      className={INPUT}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">T/C</label>
                    <input
                      type="number" min="0.01" step="0.0001" value={exchangeRate}
                      onChange={(e) => setExchangeRate(e.target.value)} disabled={busy}
                      placeholder="6.9600"
                      className={INPUT}
                    />
                  </div>
                </div>
              </section>
            ) : (
              <button
                type="button" onClick={() => setShowUsd(true)}
                className="text-sm text-muted-foreground hover:text-primary hover:underline"
              >
                + Pagado en dólares
              </button>
            )}

            {/* Notes */}
            <section className="space-y-1">
              <label className={`${SECTION} block`}>
                Notas <span className="font-normal normal-case">(opcional)</span>
              </label>
              <textarea
                value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy} rows={2}
                className={INPUT}
              />
            </section>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {!isValid && (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Falta completar para guardar:
                </p>
                <ul className="mt-1 list-inside list-disc text-xs text-muted-foreground">
                  {missing.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              {isEditing && (
                <div className="flex flex-col items-start gap-1">
                  <button
                    onClick={() => setConfirmDelete(true)} disabled={busy || !!issuedReceipt}
                    className="rounded-md border border-destructive px-3 py-2 text-sm text-destructive hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {deleting ? <Loader2 className="size-4 animate-spin" /> : 'Eliminar'}
                  </button>
                  {issuedReceipt && (
                    <p className="max-w-64 text-xs text-muted-foreground">
                      Anule el recibo {issuedReceiptNumber} antes de eliminar este diezmo.
                    </p>
                  )}
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <button
                  onClick={onClose} disabled={busy}
                  className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave} disabled={busy || !isValid}
                  title={isValid ? undefined : `Falta: ${missing.join(', ')}`}
                  className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {(saving || uploadingVoucher) ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                  {uploadingVoucher ? 'Subiendo...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={(o) => { if (!deleting) setConfirmDelete(o); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este registro de diezmo?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handleDelete(); }}
              disabled={deleting}
            >
              {deleting ? <Loader2 className="size-4 animate-spin" /> : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showIssueForm && receiptSourceId && receiptCategory && (
        <IssueReceiptForm
          lockedCategoryId={receiptCategory.id}
          initialPayerName={payerName || contextLine1}
          initialPayerCi={payerCi ?? undefined}
          initialCurrency={receiptCurrency}
          initialExchangeRate={receiptUsd ? exchangeRate : undefined}
          ministerId={ministerId}
          sourceType="tithe"
          sourceId={receiptSourceId}
          initialLines={[{
            concept_id: receiptConcept?.id ?? '',
            description: `${receiptTarget.label} de ${MONTH_NAMES_FULL[month - 1]} ${year}`,
            quantity: '1',
            unit_price: receiptTotal,
          }]}
          initialPayments={initialReceiptPayments}
          onClose={() => setShowIssueForm(false)}
          onIssued={async () => {
            setShowIssueForm(false);
            await refetchLinkedReceipt();
            setShowReceiptPreview(true);
          }}
        />
      )}

      {showReceiptPreview && linkedReceipt && (
        <ReceiptPrintPreview
          data={receiptToPrintData(linkedReceipt, publicReceiptUrl(linkedReceipt.public_token))}
          onClose={() => setShowReceiptPreview(false)}
        />
      )}
    </>
  );
}
